import React, { useCallback } from "react";
import loginModalStore from "~/common/LoginModal/loginModalStore";
import {
  PlusModalData,
  PlusSubscriptionModalStore,
  ModalTypes,
} from "~/features/plus/ui/modal/PlusSubscriptionModal";
import { User } from "~/common/Store/connectUserAndChat";

/**
 * Custom React hook for handling the logic to launch the Plus Subscription modal.
 *
 * This hook determines if the user is eligible to see the Plus modal based on their registration status
 * and the presence of valid addresses in the provided PlusModalData. It returns a handler that
 * either opens the login modal (if the user is not registered) or opens the appropriate Plus modal
 * (eligible or ineligible) based on the user's data. It also concatenates the order number
 * to the login modal success query string, if the method is provided one.
 *
 * The returned handler accepts any React SyntheticEvent (mouse, keyboard, form, etc.), or can be called with no argument.
 * This allows you to use it as an event handler (e.g., for button clicks, keyboard events, or form submissions)
 * or to invoke it programmatically (such as on page load).
 *
 * @param {PlusModalData} [plusModalData] - Data containing valid addresses for Plus eligibility.
 * @param {User} [user] - The current user object.
 * @param {string} [encryptedOrderNumber] - The optional order number for the plus membership purchase.
 * @returns An object with a `handlePlusModalLaunch` function for launching the modal.
 *
 * @example
 * // Using with a button (e.g., mouse event)
 * const { handlePlusModalLaunch } = usePlusModalLauncher(plusModalData, user);
 * <button onClick={handlePlusModalLaunch}>Join Plus</button>
 * @example
 * // Calling directly (e.g., on page load)
 * useEffect(() => {
 *   if (shouldShowPlusModal) {
 *     handlePlusModalLaunch();
 *   }
 * }, [shouldShowPlusModal]);
 */
const usePlusModalLauncher = (
  plusModalData?: PlusModalData,
  user?: User,
  encryptedOrderNumber?: string,
  shouldShowRestoreLink?: boolean,
) => {
  const { setModalOpen } = PlusSubscriptionModalStore.getState();

  const handlePlusModalLaunch = useCallback(
    (event?: React.SyntheticEvent<Element, Event>) => {
      event?.preventDefault();

      let queryParams = "?showform=1";

      if (encryptedOrderNumber) {
        queryParams += `&order=${encryptedOrderNumber}`;
      }

      if (!user?.registered) {
        loginModalStore.openLogin({
          targetUrl: window.location.pathname,
          queryParams: queryParams,
        });
        return;
      }

      const hasValidAddresses =
        !!plusModalData?.validAddresses?.length || shouldShowRestoreLink;
      setModalOpen(hasValidAddresses ? ModalTypes.PLUS : ModalTypes.INELIGIBLE);
    },
    [
      encryptedOrderNumber,
      user?.registered,
      plusModalData?.validAddresses?.length,
      shouldShowRestoreLink,
      setModalOpen,
    ],
  );

  return { handlePlusModalLaunch };
};

export default usePlusModalLauncher;
